/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { withTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, Card, CardContent } from '@mui/material';
import { isEmpty } from 'modules/common/helpers/object';
import { grey } from '@mui/material/colors';
import { Loader } from 'modules/common/components';
import { configureEventCategoriesActions } from 'modules/event-configuration/slice';
import {
  selectEventCategoryConfigurations,
  selectLoader,
} from 'modules/event-configuration/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import PAGINATION from 'modules/common/constants/pagination-limits';
import MinorEventDataDrawer from '../minor-event-data-drawer';
import MajorEventsTableView from '../major-events-table';
/**
 * Main view for event category configuration layout
 * @param {*} theme - application theme configuration
 */
const EventCategoryConfigurationView = ({ theme }) => {
  const dispatch = useDispatch();
  // global selectors
  const loading = useSelector(selectLoader);
  const organizationId = useSelector(selectOrganizationId);
  const eventCategories = useSelector(selectEventCategoryConfigurations);
  //
  const [show, setShow] = useState(false);
  const [selectedEventCategoryId, setSelectedEventCategoryId] = useState(null);
  // triggers when component renders for first time
  useEffect(() => {
    // adjust window position to 0,0
    window.scrollTo(0, 0);
    // get configure event categories
    const query = `limit=${PAGINATION.MAJOR_EVENT_CATEGORIES_PER_PAGE}`;
    dispatch(
      configureEventCategoriesActions.configureEventCategories({
        organizationId,
        query,
      })
    );
  }, []);
  //
  return (
    <Loader loading={loading}>
      <Box>
        <Grid className="sub-title-event-config" item xs={4} padding={5}>
          <Typography variant="h2">Configure event categories</Typography>
          <Grid container direction="row">
            <Typography variant="h4" sx={{ marginRight: 5 }}>
              Major event categories{' '}
            </Typography>
            <Typography variant="h6" sx={{ color: grey[500] }}>
              ( {eventCategories?.totalResults} categories )
            </Typography>
          </Grid>
        </Grid>
        <Card>
          <CardContent
            sx={{
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid container direction="row" spacing={2} justifyContent="space-between">
              <Grid item sx={{ flexGrow: 1, flexBasis: 1 }}>
                {!isEmpty(eventCategories) && (
                  <MajorEventsTableView
                    isShow={setShow}
                    setSelectedMajorEventId={setSelectedEventCategoryId}
                  />
                )}
              </Grid>
              <MinorEventDataDrawer
                show={show}
                isShow={setShow}
                majorEventCategoryId={selectedEventCategoryId}
              />
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Loader>
  );
};
//
export default withTheme(EventCategoryConfigurationView);
